import { findNode, findParentList, type CorpusNode } from '../stores/corpus'

export interface PageNamingScheme {
  /**
   * true: every folder counts from 1 on its own (Scene 01, 02... in each chapter folder).
   * false: numbers continue across all folders in the desk (Chapter 1...N across Part One, Part Two).
   * Undefined means the user never chose; see restartsPerFolder().
   */
  restartPerFolder?: boolean
  id: string
  name: string
  template: string
}

export const defaultPageNamingSchemes: PageNamingScheme[] = [
  { id: 'default-chapter', name: 'Numbered chapters', template: 'Chapter %n%' },
  { id: 'default-scene', name: 'Padded scenes', template: 'Scene %n:02%', restartPerFolder: true },
  { id: 'default-journal', name: 'Daily journal', template: 'Journal %date:YYYY-MM-DD%' },
  { id: 'default-daily-note', name: 'Readable date', template: '%weekday%, %month-name% %day%, %year%' },
  { id: 'default-timestamped-note', name: 'Timestamped notes', template: 'Note %date:YYYY-MM-DD% %time:24%' },
]

export interface PageNamingContext {
  counter: number
  containerName?: string
  folderName?: string
  deskName?: string
  now?: Date
  uuid?: string
}

const pad = (value: number, width = 2) => String(value).padStart(width, '0')

function toRoman(value: number): string {
  const pairs: Array<[number, string]> = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
    [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ]
  let number = Math.max(1, Math.floor(value))
  let result = ''
  for (const [amount, numeral] of pairs) {
    while (number >= amount) {
      result += numeral
      number -= amount
    }
  }
  return result
}

function toAlpha(value: number): string {
  let number = Math.max(1, Math.floor(value))
  let result = ''
  while (number > 0) {
    number--
    result = String.fromCharCode(65 + (number % 26)) + result
    number = Math.floor(number / 26)
  }
  return result
}

function formatDate(date: Date, format: string): string {
  if (format === 'short') return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date)
  if (format === 'long') return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'long', day: 'numeric' }).format(date)
  const replacements: Record<string, string> = {
    YYYY: String(date.getFullYear()),
    YY: pad(date.getFullYear() % 100),
    MM: pad(date.getMonth() + 1),
    DD: pad(date.getDate()),
  }
  return format.replace(/YYYY|YY|MM|DD/g, token => replacements[token])
}

export function formatPageName(template: string, context: PageNamingContext): string {
  const date = context.now || new Date()
  const uuid = context.uuid || (globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`)
  return template.replace(/%([^%]+)%/g, (whole, rawToken: string) => {
    const token = rawToken.toLowerCase()
    if (token === 'n') return String(context.counter)
    if (token.startsWith('n:')) {
      const widthToken = token.slice(2)
      const width = /^0+$/.test(widthToken) ? widthToken.length : Number.parseInt(widthToken, 10)
      return pad(context.counter, Number.isFinite(width) ? Math.max(1, width) : 2)
    }
    if (token === 'roman') return toRoman(context.counter)
    if (token === 'alpha') return toAlpha(context.counter)
    if (token === 'date') return formatDate(date, 'YYYY-MM-DD')
    if (token.startsWith('date:')) return formatDate(date, rawToken.slice(5))
    if (token === 'time' || token === 'time:24') return `${pad(date.getHours())}-${pad(date.getMinutes())}`
    if (token === 'time:12') return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true }).format(date).replace(':', '-').replace(/\s+/g, ' ')
    if (token === 'year') return String(date.getFullYear())
    if (token === 'month') return pad(date.getMonth() + 1)
    if (token === 'month-name') return new Intl.DateTimeFormat(undefined, { month: 'long' }).format(date)
    if (token === 'month-short') return new Intl.DateTimeFormat(undefined, { month: 'short' }).format(date)
    if (token === 'day') return pad(date.getDate())
    if (token === 'weekday') return new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(date)
    if (token === 'weekday-short') return new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(date)
    if (token === 'container') return context.containerName || ''
    if (token === 'folder') return context.folderName || context.containerName || ''
    if (token === 'desk') return context.deskName || ''
    if (token === 'timestamp') return String(date.getTime())
    if (token === 'uuid') return uuid
    return whole
  }).trim()
}

type SequenceKind = 'n' | 'roman' | 'alpha'

/** Which counter a `%token%` produces, or null when it isn't a sequence token. */
function sequenceKind(token: string): SequenceKind | null {
  if (token === 'n' || token.startsWith('n:')) return 'n'
  if (token === 'roman') return 'roman'
  if (token === 'alpha') return 'alpha'
  return null
}

function fromRoman(text: string): number | null {
  const values: Record<string, number> = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 }
  let total = 0
  for (let i = 0; i < text.length; i++) {
    const current = values[text[i]]
    if (!current) return null
    const next = values[text[i + 1]] ?? 0
    total += current < next ? -current : current
  }
  // Round-trip so only canonical numerals (and only uppercase) count.
  return total > 0 && toRoman(total) === text ? total : null
}

function fromAlpha(text: string): number | null {
  let total = 0
  for (const char of text) total = total * 26 + (char.charCodeAt(0) - 64)
  return total > 0 && toAlpha(total) === text ? total : null
}

const sequenceParsers: Record<SequenceKind, (raw: string) => number | null> = {
  n: raw => Number.parseInt(raw, 10),
  roman: fromRoman,
  alpha: fromAlpha,
}

const sequencePatterns: Record<SequenceKind, string> = {
  n: '\\d+',
  roman: '[ivxlcdm]+',
  alpha: '[a-z]{1,2}',
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

type SequenceMatcher = (pageName: string) => number | null

/**
 * Turns a naming template into a function that reads the sequence number back
 * out of an existing page name (or returns null if the name isn't part of the
 * sequence). This is the inverse of formatPageName.
 *
 * - Text before the number must match, so "Prologue" or "Chapter 5 notes" is
 *   never mistaken for a chapter unless the template says so.
 * - Anything after the number is ignored, so "Chapter 3: The Storm" still
 *   counts as 3 after the writer gives a chapter a real title.
 * - %container% / %folder% / %desk% are matched literally, so a template that
 *   names the folder keeps a separate sequence per folder.
 * - Other tokens (dates, times, uuids...) match anything.
 *
 * Returns null when the template has no sequence token.
 */
function buildSequenceMatcher(
  template: string,
  context: Pick<PageNamingContext, 'containerName' | 'folderName' | 'deskName'>,
): SequenceMatcher | null {
  let source = ''
  let cursor = 0
  let end = 0
  let firstKind: SequenceKind | null = null

  for (const match of template.matchAll(/%([^%]+)%/g)) {
    const index = match.index ?? 0
    source += escapeRegExp(template.slice(cursor, index))
    cursor = index + match[0].length

    const token = match[1].toLowerCase()
    const kind = sequenceKind(token)
    if (kind) {
      // Only the first sequence token is captured; repeats just have to match.
      source += firstKind ? `(?:${sequencePatterns[kind]})` : `(${sequencePatterns[kind]})`
      if (!firstKind) firstKind = kind
      end = source.length
    } else if (token === 'container') {
      source += escapeRegExp(context.containerName || '')
    } else if (token === 'folder') {
      source += escapeRegExp(context.folderName || context.containerName || '')
    } else if (token === 'desk') {
      source += escapeRegExp(context.deskName || '')
    } else {
      source += '.+?'
    }
  }

  if (!firstKind) return null
  const parse = sequenceParsers[firstKind]
  // Cut the pattern off after the last sequence token, then make sure the
  // number isn't glued to more letters/digits ("Chapter 3rd", "Chapter Mix").
  const pattern = new RegExp(`^${source.slice(0, end)}(?![\\p{L}\\p{N}])`, 'iu')
  return pageName => {
    const found = pattern.exec(pageName.trim())
    return found ? parse(found[1]) : null
  }
}

/**
 * Walks every page in the numbering scope and reports the highest sequence
 * number already used, plus every page name (for the uniqueness check).
 *
 * Scope: everything inside `scopeRoot` (the enclosing desk), or the whole tree
 * when there is no desk. Only pages whose *effective* scheme is `schemeId`
 * contribute numbers, using the same nearest-ancestor rule createPageName uses
 * to pick a scheme. That way chapters split across several folders share one
 * sequence, while a "Notes" folder with no scheme (or a different one) doesn't
 * inflate it.
 */
function collectSequenceScope(
  tree: CorpusNode[],
  scopeRoot: CorpusNode | null,
  assignments: Record<string, string>,
  schemeId: string,
  readNumber: SequenceMatcher,
): { names: Set<string>; highest: number } {
  const names = new Set<string>()
  let highest = 0

  const visit = (nodes: CorpusNode[], inherited: string | undefined, inScope: boolean) => {
    for (const node of nodes) {
      if (node.subtype === 'page') {
        if (!inScope) continue
        names.add(node.name)
        if (inherited === schemeId) {
          const value = readNumber(node.name)
          if (value !== null && value > highest) highest = value
        }
        continue
      }
      visit(
        node.children ?? [],
        assignments[node.id] || inherited,
        inScope || node.id === scopeRoot?.id,
      )
    }
  }

  visit(tree, undefined, scopeRoot === null)
  return { names, highest }
}

/**
 * Schemes saved by older versions of the app have no value. The built-in scene
 * scheme always restarted in each folder, so it keeps doing so; every other
 * scheme continues across folders unless the user turns the option on.
 */
export function restartsPerFolder(scheme: PageNamingScheme): boolean {
  return scheme.restartPerFolder ?? scheme.id === 'default-scene'
}

/**
 * Per-folder numbering: only pages sitting directly in `container` count, so
 * every folder runs its own sequence starting at 1.
 */
function collectFolderScope(
  container: CorpusNode,
  readNumber: SequenceMatcher,
): { names: Set<string>; highest: number } {
  const names = new Set<string>()
  let highest = 0
  for (const child of container.children ?? []) {
    if (child.subtype !== 'page') continue
      names.add(child.name)
      const value = readNumber(child.name)
      if (value !== null && value > highest) highest = value
  }
  return { names, highest }
}

function ancestry(tree: CorpusNode[], node: CorpusNode): CorpusNode[] {
  const result: CorpusNode[] = [node]
  let current = node
  while (true) {
    const parent = findParentList(tree, current.id)?.parent
    if (!parent) break
    result.push(parent)
    current = parent
  }
  return result
}

export function createPageName(options: {
  tree: CorpusNode[]
  parentId: string | null
  schemes: PageNamingScheme[]
  assignments: Record<string, string>
  now?: Date
}): string {
  if (!options.parentId) return 'New Page'
  const target = findNode(options.tree, options.parentId)
  if (!target) return 'New Page'
  const targetContainer = target.subtype === 'page'
    ? findParentList(options.tree, target.id)?.parent || null
    : target
  if (!targetContainer) return 'New Page'

  const chain = ancestry(options.tree, targetContainer)
  const schemeContainer = chain.find(node => options.assignments[node.id])
  if (!schemeContainer) return 'New Page'
  const scheme = options.schemes.find(item => item.id === options.assignments[schemeContainer.id])
  if (!scheme) return 'New Page'

  const folder = chain.find(node => node.subtype === 'folder')
  const desk = chain.find(node => node.subtype === 'desk')
  const context = {
    containerName: targetContainer.name,
    folderName: folder?.name,
    deskName: desk?.name,
    now: options.now,
  }

  // Continue from the highest number already in use rather than counting
  // pages: a Prologue, an Epilogue or a renamed chapter must not shift the
  // sequence, and chapters may live in any number of folders.
  const readNumber = buildSequenceMatcher(scheme.template, context)
  const hasSequence = readNumber !== null
  let counter = 1
  let names = new Set<string>()
  if (readNumber) {
    const scope = restartsPerFolder(scheme)
    ? collectFolderScope(targetContainer, readNumber)
    : collectSequenceScope(options.tree, desk ?? null, options.assignments, scheme.id, readNumber)
    names = scope.names
    counter = scope.highest + 1
  }

  const firstCounter = counter
  let candidate = ''
  do {
    candidate = formatPageName(scheme.template, { counter, ...context }) || 'New Page'
    counter++
  } while (hasSequence && names.has(candidate) && counter < firstCounter + 10001)
  return candidate
}
